/**
 * <hero-video>: the background video of the hero section.
 *
 * The poster image underneath stays the LCP element. The video only starts
 * loading once that image has loaded, and fades in when its first frame is
 * actually playing. Visitors who ask for reduced motion keep the poster and
 * get a play button instead of autoplay. The video pauses while the hero is
 * scrolled out of view, and the toggle button pauses and resumes it.
 */
if (!customElements.get('hero-video')) {
  class HeroVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      this.toggleButton = this.querySelector('[data-hero-video-toggle]');
      this.started = false;
      this.inView = true;
      this.pausedByUser = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.video.addEventListener('playing', () => this.classList.add('is-playing'));
      this.video.addEventListener('play', () => this.setToggleState(true));
      this.video.addEventListener('pause', () => this.setToggleState(false));
      this.toggleButton?.addEventListener('click', this.onToggleClick.bind(this));

      this.observer = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) {
          this.play();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      });
      this.observer.observe(this);

      const poster = this.parentElement?.querySelector('img');
      if (!poster || poster.complete) {
        this.start();
      } else {
        poster.addEventListener('load', () => this.start(), { once: true });
        poster.addEventListener('error', () => this.start(), { once: true });
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    start() {
      if (this.started) return;
      this.started = true;

      if (this.pausedByUser) {
        this.setToggleState(false);
      } else {
        this.play();
      }
    }

    play() {
      if (!this.started || this.pausedByUser || !this.inView) return;

      this.video.muted = true;
      const promise = this.video.play();
      if (promise) promise.catch(() => this.setToggleState(false));
    }

    onToggleClick() {
      if (this.video.paused) {
        this.pausedByUser = false;
        this.started = true;
        this.play();
      } else {
        this.pausedByUser = true;
        this.video.pause();
      }
    }

    setToggleState(isPlaying) {
      if (!this.toggleButton) return;

      this.toggleButton.classList.toggle('is-paused', !isPlaying);
      this.toggleButton.setAttribute(
        'aria-label',
        isPlaying ? this.toggleButton.dataset.labelPause : this.toggleButton.dataset.labelPlay
      );
    }
  }

  customElements.define('hero-video', HeroVideo);
}
